/**
 * Single internal-operator realtime DTO family for live dashboard (deny-by-default; no raw internalAgenda).
 *
 * @module core/types/internalOperatorRealtime
 */

import type { OperatorInterventionMode, InternalAgendaLifecycleStatus } from './operatorIntervention';

/** Re-export for consumers that imported lifecycle from this module. */
export type { InternalAgendaLifecycleStatus } from './operatorIntervention';

/** Contract id — one envelope kind for all live internal-operator pushes. */
export const INTERNAL_OPERATOR_REALTIME_KIND = 'INTERNAL_OPERATOR_REALTIME_V1' as const;

/**
 * Evidence derived from canonical meetingOutputs + decisions.consolidated only (no Layer 2).
 * Machine-friendly ids for the internal operator dashboard.
 */
export type InternalOperatorEvidenceChip =
  | 'decision_captured'
  | 'action_item_captured'
  | 'follow_up_captured'
  | 'topic_moved_on'
  | 'unresolved'
  | 'none';

/**
 * Stable machine-readable reasons explaining why a row landed in its current lifecycle state.
 * These are bounded summaries of public-topic progress only; they never expose raw private goal text.
 */
export type InternalOperatorStatusReasonCode =
  | 'no_public_topic_link'
  | 'linked_topic_not_in_live_agenda'
  | 'linked_topic_not_reached'
  | 'linked_topic_active_without_decision'
  | 'linked_topic_has_decision'
  | 'linked_topic_has_action_or_follow_up_only'
  | 'agenda_drift_elevated'
  | 'linked_topic_advanced_without_decision';

export interface InternalOperatorRealtimeItemV1 {
  internalAgendaItemId: string;
  /** Public agenda link when present; drives derivation. */
  linkedPublicAgendaItemId: string | null;
  lifecycleStatus: InternalAgendaLifecycleStatus;
  /** Short deterministic explanation for operators; never includes raw private-goal text. */
  statusSummary: string;
  /** Stable reason codes behind {@link lifecycleStatus}. */
  statusReasonCodes: InternalOperatorStatusReasonCode[];
  evidenceChips: InternalOperatorEvidenceChip[];
  /** True when operator set a manual lifecycle override for this row (display only). */
  hasLifecycleOverride: boolean;
}

export interface InternalOperatorWarningV1 {
  warningId: string;
  code: string;
  internalAgendaItemId: string | null;
  linkedPublicAgendaItemId: string | null;
  severity: 'info' | 'warning' | 'critical';
  /** Operator-facing explanation for the warning. */
  message: string;
}

export interface InternalOperatorPendingRecommendationV1 {
  recommendationId: string;
  code: string;
  internalAgendaItemId: string | null;
  linkedPublicAgendaItemId: string | null;
  prompt: string;
  /** Stable reason codes behind the recommendation. */
  reasonCodes: InternalOperatorStatusReasonCode[];
  /** Scaffolding for Auto / Manual / Hybrid gating. */
  riskTier: 'low' | 'sensitive';
  /** Whether this recommendation blocks until explicit operator action (derived from mode + tier). */
  requiresApproval: boolean;
}

/**
 * Bounded Layer 2 support hints for internal operator (approved {@link Layer2ReadPort} reads only).
 * Additive — never authoritative for status, evidence, or coverage.
 */
export interface InternalOperatorLayer2SupportV1 {
  schemaVersion: 1;
  source: 'layer2_read_port_approved';
  team: null | {
    maturityLevel: 'nascent' | 'emerging' | 'mature';
    /** Normalized 0–1 */
    confidence01: number;
    insufficientEvidence: boolean;
    sparseSignal: boolean;
    engagementTrend: string | null;
  };
  org: null | {
    compositionMaturityLevel: 'nascent' | 'emerging' | 'mature';
    profileConfidence01: number;
    insufficientEvidence: boolean;
  };
  recurringSeries: null | {
    coldStartStage: 'cold_start' | 'warming' | 'established';
    confidence01: number;
    insufficientEvidence: boolean;
  };
  learningAux: {
    approvedSignatureCount: number;
    approvedRecommendationCount: number;
    maxRecentRecommendationConfidence01: number | null;
  };
}

/**
 * Single envelope for WebSocket `INTERNAL_OPERATOR_REALTIME_V1` — the only internal realtime payload family in Phase 1.
 */
export interface InternalOperatorRealtimeEnvelopeV1 {
  kind: typeof INTERNAL_OPERATOR_REALTIME_KIND;
  schemaVersion: 1;
  meetingId: string;
  serverTimestampMs: number;
  /** Resolved active mode (meeting wins, else user default). */
  activeInterventionMode: OperatorInterventionMode;
  /** True when `state.operatorIntervention.meetingMode` is non-null. */
  meetingInterventionModeExplicit: boolean;
  items: InternalOperatorRealtimeItemV1[];
  warnings: InternalOperatorWarningV1[];
  pendingRecommendations: InternalOperatorPendingRecommendationV1[];
  /**
   * Optional Layer 2 learning support hints (approved port reads only). Omitted when unavailable / fail-closed.
   * Does not affect items, warnings, or pendingRecommendations derivation.
   */
  layer2Support?: InternalOperatorLayer2SupportV1 | null;
  /** Late-start detection state. Present only when late start was detected for this meeting. */
  lateStart?: InternalOperatorLateStartV1 | null;
}

/** Adaptive late-start tier — drives UI behavior. */
export type LateStartTier = 'absorbed' | 'nudge' | 'guided' | 'triage';

/** Per-item adjustment detail pushed to the organizer dashboard. */
export interface LateStartItemAdjustmentV1 {
  agendaItemId: string;
  title: string;
  originalDurationSec: number;
  adjustedDurationSec: number;
  /** Derived compression priority (0–1, higher = more protected). */
  priority: number;
}

/** Late-start detection state pushed to the organizer dashboard. */
export interface InternalOperatorLateStartV1 {
  /** Adaptive tier — controls dashboard UX level. */
  tier: LateStartTier;
  /** Legacy severity kept for backwards compat. */
  severity: 'mild' | 'moderate' | 'severe';
  lateMinutes: number;
  /** Fraction of meeting time lost (0–1). */
  impactRatio: number;
  /** null = not yet resolved or auto-applied, non-null = resolved. */
  resolution: 'compress' | 'keep_durations' | 'rithmo_suggests' | null;
  /** True when organizer input is still awaited (guided/triage tiers only). */
  pendingChoice: boolean;
  /** Ms until auto-timeout fires (0 if already resolved or not applicable). */
  expiresInMs: number;
  /** True when the organizer can undo the auto-applied adjustment. */
  canUndo: boolean;
  /** Per-item adjustment details (populated after compression is applied). */
  itemAdjustments: LateStartItemAdjustmentV1[];
}
